import type { TextProvider } from './text';

type ShortCuts = [
  {
    /** Name of the shortcut (for display purposes) */
    name: string;
    /**
     * Key bindings for the shortcut. A string represents a key combination, e.g., "ctrl + b", "cmd + b", "alt + shift + s".
     *
     * To ensure consistent semantics, use the following standard key names:
     * - ctrl (Control)
     * - meta (Windows key on Windows, Command key on macOS)
     * - alt (Alt on Windows, Option on macOS)
     * - shift
     *
     * All keys should be in lowercase. Arrow keys use glyphs: ← → ↑ ↓.
     * The same shortcut can have multiple bindings. If an object is provided, it can specify different bindings for Windows and macOS.
     * If a string is provided, it applies to all platforms.
     */
    bindings:
      | string
      | {
          any?: string | string[];
          win?: string | string[];
          mac?: string | string[];
        };
    allowShift?: boolean;
  },
  handler: (e: KeyboardEvent, provider: TextProvider) => void,
][];

export const shortCuts: ShortCuts = [
  // Move cursor by character
  [
    { name: 'Move cursor left', bindings: '←', allowShift: true },
    (e, provider) => {
      if (!provider.isCollapsed && !e.shiftKey) {
        provider.collapse(provider.isFocusBehindAnchor ? 'anchor' : 'focus');
        return;
      }
      provider.moveCursor(-1, !!e.shiftKey);
    },
  ],
  [
    { name: 'Move cursor right', bindings: '→', allowShift: true },
    (e, provider) => {
      if (!provider.isCollapsed && !e.shiftKey) {
        provider.collapse(provider.isFocusBehindAnchor ? 'focus' : 'anchor');
        return;
      }
      provider.moveCursor(1, !!e.shiftKey);
    },
  ],
  [
    {
      name: 'Move cursor up',
      bindings: { any: '↑', mac: 'ctrl + p' },
      allowShift: true,
    },
    (e, provider) => {
      provider.moveCursorVertical(-1, !!e.shiftKey);
    },
  ],
  [
    {
      name: 'Move cursor down',
      bindings: { any: '↓', mac: 'ctrl + n' },
      allowShift: true,
    },
    (e, provider) => {
      provider.moveCursorVertical(1, !!e.shiftKey);
    },
  ],

  // Move by word
  [
    {
      name: 'Move by word left',
      bindings: { win: 'ctrl + ←', mac: 'alt + ←' },
    },
    (e, provider) => {
      provider.moveCursorToWordBoundary(-1, !!e.shiftKey);
    },
  ],
  [
    {
      name: 'Move by word right',
      bindings: { win: 'ctrl + →', mac: 'alt + →' },
    },
    (e, provider) => {
      provider.moveCursorToWordBoundary(1, !!e.shiftKey);
    },
  ],

  // Move by paragraph
  [
    {
      name: 'Move by paragraph up',
      bindings: { win: 'ctrl + ↑', mac: 'alt + ↑' },
      allowShift: true,
    },
    (e, provider) => {
      // Paragraph jump not implemented; fall back to line up
      provider.moveCursorVertical(-1, !!e.shiftKey);
    },
  ],
  [
    {
      name: 'Move by paragraph down',
      bindings: { win: 'ctrl + ↓', mac: 'alt + ↓' },
      allowShift: true,
    },
    (e, provider) => {
      // Paragraph jump not implemented; fall back to line down
      provider.moveCursorVertical(1, !!e.shiftKey);
    },
  ],

  // Select all
  [
    {
      name: 'Select all',
      bindings: { win: 'ctrl + a', mac: 'meta + a' },
    },
    (_e, provider) => {
      provider.selectAll();
    },
  ],

  // Move to line boundaries
  [
    {
      name: 'Move to line start',
      bindings: { win: 'home', mac: ['meta + ←', 'ctrl + a'] },
      allowShift: true,
    },
    (_e, provider) => {
      provider.moveCursorToLineStart(!!_e.shiftKey);
    },
  ],
  [
    {
      name: 'Move to line end',
      bindings: { win: 'end', mac: ['meta + →', 'ctrl + e'] },
      allowShift: true,
    },
    (_e, provider) => {
      provider.moveCursorToLineEnd(!!_e.shiftKey);
    },
  ],

  // Move to document boundaries
  [
    {
      name: 'Move to document start',
      bindings: { win: 'ctrl + home', mac: 'meta + ↑' },
      allowShift: true,
    },
    (_e, provider) => {
      provider.moveCursorToDocumentStart(!!_e.shiftKey);
    },
  ],
  [
    {
      name: 'Move to document end',
      bindings: { win: 'ctrl + end', mac: 'meta + ↓' },
      allowShift: true,
    },
    (_e, provider) => {
      provider.moveCursorToDocumentEnd(!!_e.shiftKey);
    },
  ],

  // Editing shortcuts
  [
    {
      name: 'Delete by cut',
      bindings: { win: 'ctrl + x', mac: 'meta + x' },
    },
    (_e, provider) => {
      provider.cut();
    },
  ],
  [
    {
      name: 'Undo',
      bindings: { win: 'ctrl + z', mac: 'meta + z' },
    },
    (_e, provider) => {
      provider.undo();
    },
  ],
  [
    {
      name: 'Redo',
      bindings: { win: 'ctrl + y', mac: 'meta + shift + z' },
    },
    (_e, provider) => {
      provider.redo();
    },
  ],
];

export type BindingHandler = (e: KeyboardEvent, provider: TextProvider) => void;
export type BindingMap = {
  any: Map<string, BindingHandler>;
  win: Map<string, BindingHandler>;
  mac: Map<string, BindingHandler>;
};

const MOD_ORDER: Array<'shift' | 'ctrl' | 'alt' | 'meta'> = ['shift', 'ctrl', 'alt', 'meta'];

function normalizeBindingString(binding: string): string {
  // tokenize, trim and lowercase
  const parts = binding
    .split('+')
    .map((p) => p.trim().toLowerCase())
    .filter(Boolean);

  const mods = { shift: false, ctrl: false, alt: false, meta: false };
  let base = '';

  const tokenToBase = (t: string): string | null => {
    if (t === '←' || t === 'arrowleft') return '←';
    if (t === '→' || t === 'arrowright') return '→';
    if (t === '↑' || t === 'arrowup') return '↑';
    if (t === '↓' || t === 'arrowdown') return '↓';
    if (t === 'home') return 'home';
    if (t === 'end') return 'end';
    if (t.length === 1) return t; // single character like 'a', 'e', 'n', 'p'
    return null;
  };

  for (const p of parts) {
    if (p === 'shift') mods.shift = true;
    else if (p === 'ctrl' || p === 'control') mods.ctrl = true;
    else if (p === 'alt' || p === 'option') mods.alt = true;
    else if (p === 'meta' || p === 'cmd' || p === 'command') mods.meta = true;
    else {
      const b = tokenToBase(p);
      if (b) base = b;
    }
  }

  if (!base) return '';
  const modList: string[] = [];
  for (const m of MOD_ORDER) if (mods[m]) modList.push(m);
  return modList.length ? `${modList.join(' + ')} + ${base}` : base;
}

function normalizeEventToBinding(e: KeyboardEvent): string {
  // derive base
  const k = e.key;
  let base = '';
  switch (k) {
    case 'ArrowLeft':
      base = '←';
      break;
    case 'ArrowRight':
      base = '→';
      break;
    case 'ArrowUp':
      base = '↑';
      break;
    case 'ArrowDown':
      base = '↓';
      break;
    case 'Home':
      base = 'home';
      break;
    case 'End':
      base = 'end';
      break;
    case 'Shift':
    case 'Control':
    case 'Alt':
    case 'Meta':
      base = ''; // pure modifier, ignore
      break;
    default: {
      if (k && k.length === 1) base = k.toLowerCase();
      else base = '';
    }
  }
  if (!base) return '';

  const mods: Set<string> = new Set();
  if (e.shiftKey) mods.add('shift');
  if (e.ctrlKey) mods.add('ctrl');
  if (e.altKey) mods.add('alt');
  if (e.metaKey) mods.add('meta');

  // order modifiers canonically
  const sorted = MOD_ORDER.filter((m) => mods.has(m));
  return sorted.length ? `${sorted.join(' + ')} + ${base}` : base;
}

function getPlatform(): 'win' | 'mac' {
  if (typeof navigator === 'undefined') return 'win';
  const p = navigator.platform || navigator.userAgent || '';
  return /mac|iphone|ipad|ipod/i.test(p) ? 'mac' : 'win';
}

// (builder is exported below for external use)

export function buildBindingMap(source: ShortCuts): BindingMap {
  const map: BindingMap = { any: new Map(), win: new Map(), mac: new Map() };

  for (const [meta, handler] of source) {
    const b = meta.bindings;
    const add = (platform: 'any' | 'win' | 'mac', bind: string) => {
      const key = normalizeBindingString(bind);
      if (!key) return;
      const target = platform === 'any' ? map.any : platform === 'mac' ? map.mac : map.win;
      if (!target.has(key)) target.set(key, handler);

      // if allowShift is enabled, also register a Shift-augmented variant of this binding
      if (meta.allowShift) {
        const tokens = key.split(' + ');
        const base = tokens[tokens.length - 1];
        const mods = new Set(tokens.slice(0, -1));
        mods.add('shift');
        const withShift = [...MOD_ORDER.filter((m) => mods.has(m)), base].join(' + ');
        if (!target.has(withShift)) target.set(withShift, handler);
      }
    };

    if (typeof b === 'string') {
      add('any', b);
    } else if (b && typeof b === 'object') {
      const { any, win, mac } = b as {
        any?: string | string[];
        win?: string | string[];
        mac?: string | string[];
      };
      const addMany = (platform: 'any' | 'win' | 'mac', v?: string | string[]) => {
        if (!v) return;
        if (typeof v === 'string') add(platform, v);
        else for (const it of v) add(platform, it);
      };
      addMany('any', any);
      addMany('win', win);
      addMany('mac', mac);
    }
  }
  return map;
}

export function handleShortcutEvent(
  e: KeyboardEvent,
  bindingMap: BindingMap,
  provider: TextProvider
): boolean {
  const key = normalizeEventToBinding(e);
  if (!key) return false;
  const platform = getPlatform();
  const primary = platform === 'mac' ? bindingMap.mac : bindingMap.win;
  const handler = primary.get(key) || bindingMap.any.get(key);
  if (handler) {
    handler(e, provider);
    return true;
  }
  return false;
}
